/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './StickyRailSection.module.scss';

import type { StickyRailSectionProps } from './StickyRailSection.types';

export const StickyRailSection = ({ eyebrow, title, description, blocks, className }: StickyRailSectionProps) => {
  const railRef = useRef<HTMLDivElement>(null);
  const railNodeRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const [fillHeight, setFillHeight] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    const items = blockRefs.current.filter((item): item is HTMLDivElement => item !== null);

    if (items.length === 0) {
      return undefined;
    }

    const activeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const index = items.indexOf(entry.target as HTMLDivElement);

          if (index !== -1) {
            setActive(index);
          }
        });
      },
      { rootMargin: '-32% 0px -55% 0px', threshold: 0 },
    );

    items.forEach((item) => activeObserver.observe(item));

    return () => activeObserver.disconnect();
  }, [blocks]);

  useEffect(() => {
    const measure = () => {
      const railEl = railRef.current;
      const nodeEl = railNodeRefs.current[active];

      if (!railEl || !nodeEl) {
        return;
      }

      const railRect = railEl.getBoundingClientRect();
      const nodeRect = nodeEl.getBoundingClientRect();

      setFillHeight(nodeRect.top + nodeRect.height / 2 - railRect.top);
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [active, blocks]);

  const selectBlock = (index: number) => {
    setActive(index);
    blockRefs.current[index]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <section className={cn(styles.root, className)}>
      <div className={styles.decor} aria-hidden="true">
        <span className={styles.glowA} />
        <span className={styles.glowB} />
      </div>

      <div className={styles.inner}>
        <TitleBlock extra={eyebrow} title={title} description={description} align="left" className={styles.intro} />

        <div className={styles.grid}>
          <div className={styles.rail} ref={railRef}>
            <div className={styles.railTrack} />
            <div className={styles.railFill} style={{ height: fillHeight }} />

            {blocks.map((block, i) => (
              <button
                key={block.key}
                type="button"
                className={cn(
                  styles.railItem,
                  i === active && styles.railItemActive,
                  i < active && styles.railItemDone,
                )}
                onClick={() => selectBlock(i)}
              >
                <span
                  className={styles.railNode}
                  ref={(el) => {
                    railNodeRefs.current[i] = el;
                  }}
                >
                  <span className={styles.railNodeCore} />
                </span>

                <span className={styles.railCopy}>
                  <span className={styles.railNum}>{String(i + 1).padStart(2, '0')}</span>
                  <h3>{block.headline}</h3>
                </span>
              </button>
            ))}

            <div className={styles.railProgress}>
              {String(active + 1).padStart(2, '0')} / {String(blocks.length).padStart(2, '0')}
            </div>
          </div>

          <div className={styles.body}>
            {blocks.map((block, i) => (
              <div
                key={block.key}
                ref={(el) => {
                  blockRefs.current[i] = el;
                }}
                className={styles.block}
              >
                <span className={styles.blockGhostNum} aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>

                <div className={styles.mobileHeadline}>
                  <span className={styles.railNum}>{String(i + 1).padStart(2, '0')}</span>
                  <h3>{block.headline}</h3>
                </div>

                <div className={styles.blockCopy}>
                  {block.body}
                  {block.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
